import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ActiveProjectCard = ({ project }) => {
  const getStatusColor = (status) => {
    const colors = {
      'In Progress': 'bg-primary/10 text-primary',
      'On Hold': 'bg-warning/10 text-warning',
      'Completed': 'bg-success/10 text-success',
      'Planning': 'bg-secondary/10 text-secondary'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'High': 'text-error',
      'Medium': 'text-warning',
      'Low': 'text-success'
    };
    return colors?.[priority] || 'text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6 hover:shadow-lg transition-all duration-300">
      <div className="flex items-start justify-between mb-3 md:mb-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name={project?.icon} size={20} className="md:w-6 md:h-6" color="var(--color-primary)" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm md:text-base lg:text-lg font-semibold text-foreground line-clamp-1">
              {project?.name}
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground">{project?.type}</p>
          </div>
        </div>
        <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${getStatusColor(project?.status)}`}>
          {project?.status}
        </span>
      </div>
      <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-4 line-clamp-2">
        {project?.description}
      </p>
      <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
        <div>
          <div className="flex items-center justify-between text-xs md:text-sm mb-2">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-semibold text-foreground">{project?.progress}%</span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${project?.progress}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={14} className="md:w-4 md:h-4 text-muted-foreground flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Deadline</p>
              <p className="text-xs md:text-sm font-medium text-foreground whitespace-nowrap">{project?.deadline}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={14} className="md:w-4 md:h-4 text-muted-foreground flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Priority</p>
              <p className={`text-xs md:text-sm font-medium ${getPriorityColor(project?.priority)}`}>
                {project?.priority}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Icon name="Users" size={14} className="md:w-4 md:h-4 text-muted-foreground flex-shrink-0" />
          <div className="flex -space-x-2 flex-shrink-0">
            {project?.team?.slice(0, 3)?.map((member, index) => (
              <Image
                key={index}
                src={member?.avatar}
                alt={member?.avatarAlt}
                className="w-6 h-6 md:w-8 md:h-8 rounded-full border-2 border-card object-cover"
              />
            ))}
            {project?.team?.length > 3 && (
              <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-muted border-2 border-card flex items-center justify-center">
                <span className="text-xs font-medium text-foreground">+{project?.team?.length - 3}</span>
              </div>
            )}
          </div>
          <span className="text-xs text-muted-foreground ml-2">{project?.team?.length} members</span>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="default" size="sm" fullWidth iconName="Eye" iconPosition="left">
          View Details
        </Button>
        <Button variant="outline" size="sm" iconName="MessageSquare" className="flex-shrink-0">
          <span className="sr-only">Message</span>
        </Button>
      </div>
    </div>
  );
};

export default ActiveProjectCard;